import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
} from '@knockrentals/knock-react';
import SourcesAPI from './SourcesAPI';

class PropertySourceCosts extends Component {
  state = {
    isLoading: true,
    fees: [],
  };

  MONTH_OFFSET = 1;
  feeDateFormatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
  });
  _currentDate = new Date();
  currentDateToCompare = new Date(
    this._currentDate.getFullYear(),
    this._currentDate.getMonth()
  );

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    const hasSourceChanged =
      this.props.source.propertySourceId !== prevProps.source.propertySourceId;
    if (hasSourceChanged) {
      this.loadData();
    }
  }

  loadData() {
    this.setState({ isLoading: true });

    SourcesAPI.getPropertySourcesCosts(
      this.props.property.id,
      this.props.source.id
    ).then((data) => {
      const fees = data.monthly_property_source_costs.map((monthlyData) => {
        return {
          cost: monthlyData.cost,
          type: monthlyData.pricing_model || 'None',
          year: monthlyData.year,
          month: monthlyData.month,
          isPerpetual: monthlyData.is_perpetual,
        };
      });

      this.setState({
        fees,
        originalFees: JSON.parse(JSON.stringify(fees)),
        isLoading: false,
      });

      if (fees.length > 0 && !fees[0].cost) {
        this.addFutureMonthToCurrent();
      }
    });
  }

  render() {
    return (
      <Modal
        className="property-source-costs"
        onBackdropClicked={this.props.onCancel}
      >
        <ModalHeader>
          <h3>{this.props.source.title}&apos;s Fees</h3>
        </ModalHeader>

        <ModalContent>
          <table className="wire-frame-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Cost type</th>
                <th>Cost amount</th>
                <th />
              </tr>
            </thead>

            <tbody>{this.renderCosts()}</tbody>
          </table>
          <div>
            <small>
              <i className="fa fa-fast-forward fa-fw" /> Applies perpetually to
              all future months rolling forward
            </small>
          </div>
        </ModalContent>

        <ModalFooter>
          <button className="knock-react-button" onClick={this.props.onCancel}>
            Cancel
          </button>
          <button
            className="knock-react-button"
            disabled={this.state.isLoading}
            onClick={this.saveCosts.bind(this)}
          >
            Save
          </button>
        </ModalFooter>
      </Modal>
    );
  }

  renderCosts() {
    const feeTypeOptions = this.props.feeTypes.map((feeType, index) => {
      return (
        <option key={index} value={feeType.value}>
          {feeType.label}
        </option>
      );
    });

    const getFeeTypeChangeHandler = (feeIndex) => {
      return (event) => {
        const updatedFees = this.state.fees.slice();
        updatedFees[feeIndex].type = event.target.value;
        updatedFees[feeIndex].cost = updatedFees[feeIndex].cost || 0;

        this.setState({ fees: updatedFees });
      };
    };

    const getFeeCostChangeHandler = (feeIndex) => {
      return (event) => {
        const updatedFees = this.state.fees.slice();
        updatedFees[feeIndex].cost = parseInt(event.target.value, 10) || 0;

        this.setState({ fees: updatedFees });
      };
    };

    return this.state.fees.map((fee, index) => {
      const adjustedNow = new Date(fee.year, fee.month - this.MONTH_OFFSET);

      return (
        <tr key={index}>
          <td>
            <span>{`${this.feeDateFormatter.format(adjustedNow)} `}</span>
            {fee.isPerpetual && (
              <span>
                <i
                  className="fa fa-fast-forward fa-fw"
                  title="This ad-spend pricing will be applied perpetually to all future months rolling forward"
                />
              </span>
            )}
            {fee.isPerpetual && this.currentDateToCompare > adjustedNow && (
              <div>
                <small>
                  {`Up to ${this.feeDateFormatter.format(
                    this._currentDate
                  )} and beyond`}
                </small>
              </div>
            )}
          </td>
          <td>
            <select value={fee.type} onChange={getFeeTypeChangeHandler(index)}>
              {feeTypeOptions}
            </select>
          </td>
          <td>
            {fee.type !== 'None' && (
              <input
                className="knock-react-input"
                type="number"
                value={fee.cost}
                onChange={getFeeCostChangeHandler(index)}
              />
            )}
          </td>
          <td>
            {index !== this.state.fees.length - 1 ? (
              <button
                className="knock-react-button"
                disabled={fee.type === 'None'}
                onClick={this.applyToFuture.bind(this, fee, index)}
              >
                Apply to future
              </button>
            ) : (
              <div>
                <button
                  className="knock-react-button btn-success btn-sm"
                  onClick={this.addFutureMonth.bind(this)}
                >
                  Next month
                </button>
                {this.currentDateToCompare > adjustedNow && (
                  <div style={{ paddingTop: '2px' }}>
                    <button
                      className="knock-react-button btn-success btn-sm"
                      onClick={this.addFutureMonthToCurrent.bind(this)}
                    >
                      To current month
                    </button>
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      );
    });
  }

  saveCosts() {
    const changedCosts = [];

    this.state.fees.forEach((fee, index) => {
      const feeHasChanged =
        fee.cost !== this.state.originalFees[index].cost ||
        fee.type !== this.state.originalFees[index].type;

      if (feeHasChanged) {
        changedCosts.push(fee);
      }
    });

    this.setState({ isLoading: true });
    this.props.onCostsChanged(changedCosts, this.loadData.bind(this));
  }

  applyToFuture(fee, index) {
    const feesCopy = this.state.fees.slice();

    for (let i = index; i < feesCopy.length; i++) {
      feesCopy[i].cost = fee.cost;
      feesCopy[i].type = fee.type;
    }

    this.setState({ fees: feesCopy });
  }

  addFutureMonthToCurrent() {
    let lastFee = this.state.fees[this.state.fees.length - 1];
    let lastFeeDate = new Date(lastFee.year, lastFee.month - this.MONTH_OFFSET);

    while (lastFeeDate < this.currentDateToCompare) {
      this.addFutureMonth();

      lastFee = this.state.fees[this.state.fees.length - 1];
      lastFeeDate = new Date(lastFee.year, lastFee.month - this.MONTH_OFFSET);
    }
  }

  addFutureMonth() {
    const lastFee = this.state.fees[this.state.fees.length - 1];
    const nextMonth = new Date(lastFee.year, lastFee.month);

    const nextFee = JSON.parse(JSON.stringify(lastFee));
    nextFee.month = nextMonth.getMonth() + this.MONTH_OFFSET;
    nextFee.year = nextMonth.getFullYear();
    nextFee.isPerpetual = false;

    const originalNextFee = JSON.parse(JSON.stringify(nextFee));
    originalNextFee.cost = null;
    originalNextFee.type = null;

    const fees = this.state.fees;
    fees.push(nextFee);

    const originalFees = this.state.originalFees;
    originalFees.push(originalNextFee);

    this.setState({ fees, originalFees });
  }
}

export default PropertySourceCosts;

PropertySourceCosts.propTypes = {
  feeTypes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  property: PropTypes.shape({
    data: PropTypes.object,
    id: PropTypes.number,
    leasing_team_id: PropTypes.number,
    sources: PropTypes.array,
  }),
  source: PropTypes.shape({
    propertySourceId: PropTypes.number,
    id: PropTypes.number,
    title: PropTypes.string,
  }),
  onCancel: PropTypes.func,
  onCostsChanged: PropTypes.func,
};
